"use client";

import {
  useState,
  useEffect,
  useCallback,
  startTransition,
  useRef,
  type ChangeEvent,
  type ReactNode,
} from "react";
import Link from "next/link";
import { getMuhurat } from "@/lib/api/muhurat";
import type { MuhuratResponse } from "@/lib/api/muhurat";
import { trackCalculatorUsed } from "@/lib/analytics-free-tools";
import { TimingCitySelector, type SavedCity } from "@/components/choghadiya";
import {
  RAHU_KAAL_HEADLINES,
  RahuSeoContent,
  WeeklyRahuReference,
  type CityWeeklyRahuReference,
} from "./RahuKaal.reference";
import { getTodayISTDateString } from "@/lib/ist-date";
import RahuKaalNow from "./RahuKaalNow";
import { rahuKaalTodayLine } from "@/lib/timing-today-line";
import { taggedHindi } from "@/lib/hindi-runs";

// Surfaces that are not URL-addressed keep the seeker's city in storage. The
// hubs address the city as `?city=` (owner decision 2026-09-23) and pass
// `initialCity` + `citySelector`, which bypass this store entirely.
const STORAGE_KEY = "rahu_kaal_city";

const DEFAULT_CITY_COORDS: SavedCity = {
  name: "New Delhi",
  latitude: 28.6139,
  longitude: 77.209,
};

// Restore the seeker's last city during initial state (non-addressed surfaces).
function getInitialCity(defaultCity: string): SavedCity {
  const fallback =
    defaultCity === DEFAULT_CITY_COORDS.name
      ? DEFAULT_CITY_COORDS
      : { ...DEFAULT_CITY_COORDS, name: defaultCity };
  if (typeof window === "undefined") return fallback;
  try {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (!saved) return fallback;
    const parsed = JSON.parse(saved) as SavedCity;
    if (parsed.name && parsed.latitude && parsed.longitude) return parsed;
  } catch {
    /* a corrupt or blocked store just keeps the default city */
  }
  return fallback;
}

function toDateString(date: Date): string {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

function formatDate(date: Date): string {
  return date.toLocaleDateString("en-IN", {
    weekday: "long",
    year: "numeric",
    month: "long",
    day: "numeric",
  });
}

const DAY_NAMES_HI: Record<string, string> = {
  Monday: "सोमवार",
  Tuesday: "मंगलवार",
  Wednesday: "बुधवार",
  Thursday: "गुरुवार",
  Friday: "शुक्रवार",
  Saturday: "शनिवार",
  Sunday: "रविवार",
};

export interface RahuKaalProps {
  language?: "en" | "hi";
  /**
   * Overrides the generic headline so the <h1> can name the selected city.
   *
   * A generic heading repeated across every city view is a templated-content
   * signal. `ChoghadiyaHeader` takes the same prop.
   */
  heading?: string;
  headingLevel?: "h1" | "h2";
  defaultCity?: string;
  initialDate?: string;
  initialResult?: MuhuratResponse | null;
  resultOffer: ReactNode;
  crossLinks: ReactNode;
  /**
   * City pages only. The weekly table computed from the page city's own sunrise
   * and sunset; it applies while the selector still shows that city.
   */
  cityWeeklyReference?: CityWeeklyRahuReference | null;
  /**
   * City pages pass false: their own explainer and FAQ already define Rahu Kaal,
   * so the generic "About Rahu Kaal" block was the same ~100 words a third time.
   */
  showAbout?: boolean;
  /**
   * The exact city the server rendered (`?city=` hubs). Without it a
   * non-default `defaultCity` NAME was paired with Delhi's coordinates, and the
   * seeker's stored city could replace the one the URL names.
   */
  initialCity?: SavedCity;
  /**
   * URL-addressed hubs pass their `?city=`/`?date=` selector form; it replaces
   * the in-page city search and date picker.
   */
  citySelector?: ReactNode;
  /**
   * The city's civil date at server render. Lets the server HTML reserve the
   * "right now" line's space on today's render only (no layout shift).
   */
  today?: string;
}

export function RahuKaal({
  language = "en",
  heading,
  headingLevel = "h1",
  defaultCity = "New Delhi",
  initialDate,
  initialResult = null,
  resultOffer,
  crossLinks,
  cityWeeklyReference,
  showAbout,
  initialCity,
  citySelector,
  today,
}: RahuKaalProps) {
  const [loading, setLoading] = useState(!initialResult);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<MuhuratResponse | null>(initialResult);
  const trackedInitialResult = useRef(false);
  const [selectedDate, setSelectedDate] = useState<string>(initialDate ?? toDateString(new Date()));
  const [city, setCity] = useState<SavedCity>(() => initialCity ?? getInitialCity(defaultCity));

  const handleCitySelect = useCallback((next: SavedCity) => {
    setCity(next);
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
    } catch {
      /* non-fatal: the city still applies for this visit */
    }
  }, []);

  const fetchData = useCallback(async (dateStr: string, forCity: SavedCity) => {
    startTransition(() => {
      setLoading(true);
      setError(null);
    });
    try {
      // Rule 25: the engine receives coordinates + IANA zone and returns
      // already-local time strings. Never convert times on the client.
      const response = await getMuhurat(
        dateStr,
        forCity.name,
        forCity.latitude,
        forCity.longitude,
        forCity.timezone
      );
      // Rule 62 #10: the authoritative response commits through normal
      // batched state, never a deferrable transition.
      setResult(response);
      trackCalculatorUsed({ calculator: "rahu-kaal" });
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load Rahu Kaal data");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (
      initialResult &&
      selectedDate === initialDate &&
      city.name === defaultCity &&
      !trackedInitialResult.current
    ) {
      trackedInitialResult.current = true;
      trackCalculatorUsed({ calculator: "rahu-kaal" });
      return;
    }

    fetchData(selectedDate, city);
  }, [city, defaultCity, fetchData, initialDate, initialResult, selectedDate]);

  const displayDate = new Date(selectedDate + "T00:00:00");
  const dayName = displayDate.toLocaleDateString("en-IN", { weekday: "long" });
  const dayNameHi = DAY_NAMES_HI[dayName] ?? "";
  const handleDateChange = (event: ChangeEvent<HTMLInputElement>) => {
    setSelectedDate(event.target.value);
  };

  return (
    <div className="sacred-bg min-h-[calc(100vh-130px)]">
      <div className="mx-auto max-w-2xl px-4 sm:px-6 py-12 sm:py-16">
        <RahuHeader
          language={language}
          answer={
            result ? rahuKaalTodayLine(result, city.name, getTodayISTDateString(), language) : null
          }
          heading={heading}
          headingLevel={headingLevel}
          displayDate={displayDate}
          dayNameHi={dayNameHi}
          city={city.name}
          country={city.country}
        />
        {citySelector ?? (
          <>
            <TimingCitySelector city={city} onCitySelect={handleCitySelect} />
            <RahuDatePicker selectedDate={selectedDate} onChange={handleDateChange} />
          </>
        )}

        <div className="min-h-[760px] sm:min-h-[700px]">
          {loading && <RahuLoading language={language} />}
          {error && !loading && (
            <RahuError error={error} onRetry={() => fetchData(selectedDate, city)} />
          )}
          {result && !loading && (
            <RahuResults
              result={result}
              dayName={dayName}
              resultOffer={resultOffer}
              cityWeeklyReference={cityWeeklyReference}
              isPageCity={city.name === defaultCity}
              language={language}
              today={today}
            />
          )}
        </div>

        <RahuAbout show={showAbout} language={language} />

        {crossLinks}
      </div>
    </div>
  );
}

function RahuHeader({
  answer,
  heading,
  headingLevel: Heading = "h1",
  language,
  displayDate,
  dayNameHi,
  city,
  country,
}: {
  /** The dated, city-named answer from the engine result (w3-timing-hubs). */
  answer?: string | null;
  language: "en" | "hi";
  heading?: string;
  headingLevel?: "h1" | "h2";
  displayDate: Date;
  dayNameHi: string;
  city: string;
  country?: string;
}) {
  return (
    <div className="text-center mb-10">
      <p className="text-[11px] font-semibold uppercase tracking-[0.25em] mb-3 text-intent-brand">
        Rahu Kaal Calculator
      </p>
      <Heading className="text-[36px] sm:text-[40px] font-bold leading-tight text-ish-dark font-serif">
        {heading ?? RAHU_KAAL_HEADLINES[language]}
      </Heading>
      {answer ? (
        <p className="mt-3 text-body font-semibold text-text-primary" data-timing-answer>
          {answer}
        </p>
      ) : null}
      {/* The "today's" sub-line belongs to the generic headline only: a render
          that names its own city or day (a ?date= hub, the tomorrow view) is
          not necessarily today's. */}
      {heading === undefined ? (
        <p lang="hi" className="text-[18px] mt-1 text-ish-muted font-serif">
          आज का राहु काल
        </p>
      ) : null}
      <p className="mt-2 text-body text-ish-muted">
        {formatDate(displayDate)} &middot; <span lang="hi">{dayNameHi}</span>
      </p>
      <p className="mt-1 text-caption text-ish-faint">
        {language === "hi"
          ? `${country ? `${city}, ${country}` : city} के लिए स्विस एफेमेरिस से गणना`
          : `Calculated for ${country ? `${city}, ${country}` : city} using Swiss Ephemeris`}
      </p>
    </div>
  );
}

function RahuDatePicker({
  selectedDate,
  onChange,
}: {
  selectedDate: string;
  onChange: (event: ChangeEvent<HTMLInputElement>) => void;
}) {
  return (
    <div className="flex justify-center mb-8">
      <div className="card-sacred inline-flex items-center gap-3 px-4 py-2.5">
        <label htmlFor="rahu-kaal-date" className="text-body-small font-semibold text-ish-dark">
          Check for another date:
        </label>
        <input
          id="rahu-kaal-date"
          type="date"
          value={selectedDate}
          onChange={onChange}
          className="input-sacred rounded-lg px-3 py-1.5 text-body text-ish-dark"
        />
      </div>
    </div>
  );
}

function RahuLoading({ language }: { language: "en" | "hi" }) {
  return (
    <div className="card-sacred p-8 text-center">
      <span className="inline-block w-6 h-6 border-2 border-stone-300 border-t-stone-600 rounded-full animate-spin mb-3" />
      <span className="text-[28px] sm:text-[40px] font-bold block text-ish-dark font-serif">
        {RAHU_KAAL_HEADLINES[language]}
      </span>
      <span className="mt-1 block font-serif text-[32px] font-bold text-error sm:text-[40px]">
        --:-- -- - --:-- --
      </span>
      <p className="text-body text-ish-muted">Loading Rahu Kaal timings...</p>
    </div>
  );
}

function RahuError({ error, onRetry }: { error: string; onRetry: () => void }) {
  return (
    <div className="card-sacred p-6 text-center border-error-tint bg-error/5">
      <p className="text-body text-error">{error}</p>
      <button onClick={onRetry} className="mt-3 text-body-small font-semibold text-intent-brand">
        Try Again
      </button>
    </div>
  );
}

function RahuAbout({ show = true, language = "en" }: { show?: boolean; language?: "en" | "hi" }) {
  return show ? <RahuSeoContent language={language} /> : null;
}

/** @internal */
export function RahuResults({
  result,
  dayName,
  resultOffer,
  cityWeeklyReference = null,
  isPageCity = false,
  language = "en",
  today,
}: {
  result: MuhuratResponse;
  language?: "en" | "hi";
  /** The city's civil date at server render (see `RahuKaalProps.today`). */
  today?: string;
  dayName: string;
  resultOffer: ReactNode;
  /** Applies only while the selector still shows the page's own city. */
  cityWeeklyReference?: CityWeeklyRahuReference | null;
  isPageCity?: boolean;
}) {
  return (
    <div className="space-y-6">
      {/* Today only: on now / starts in / passed, from the engine's instants. */}
      <RahuKaalNow result={result} language={language} today={today} />
      <PrimaryRahuCard result={result} language={language} />
      {/* The whole free table (Rahu Kaal, Yamagandam, Gulika Kaal) comes before
          the report offer; the offer still sits after the exact window and
          before timing depth, as its placement contract requires. */}
      <SecondaryInauspiciousSlots result={result} language={language} />
      {resultOffer}
      <PanchangSummaryCard result={result} dayName={dayName} language={language} />
      <WeeklyRahuReference
        dayName={dayName}
        cityReference={isPageCity ? cityWeeklyReference : null}
        selectedWindow={`${result.rahu_kaal.start} – ${result.rahu_kaal.end}`}
        language={language}
        isToday={today === undefined || today === result.date}
      />
      <KundaliCta language={language} />
    </div>
  );
}

function PrimaryRahuCard({ result, language }: { result: MuhuratResponse; language: "en" | "hi" }) {
  return (
    <div className="card-sacred p-6 sm:p-8 text-center border-error/35 bg-error/5">
      <span
        lang="hi"
        className="block text-[11px] font-semibold uppercase tracking-wider mb-1 text-error"
      >
        {result.rahu_kaal.label_hi}
      </span>
      <span className="block text-body-small font-semibold uppercase tracking-wider mb-3 text-error">
        Rahu Kaal
      </span>
      <span className="text-[32px] sm:text-[40px] font-bold block text-error font-serif">
        {result.rahu_kaal.start} - {result.rahu_kaal.end}
      </span>
      <p className="mt-3 text-body-small text-error">
        {language === "hi"
          ? "इस समय में नए कार्य आरंभ करने से बचें"
          : "Avoid starting new activities during this period"}
      </p>
    </div>
  );
}

function SecondaryInauspiciousSlots({
  result,
  language,
}: {
  result: MuhuratResponse;
  language: "en" | "hi";
}) {
  const hi = language === "hi";
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
      <SecondarySlotCard
        label="Yamagandam"
        labelHi={result.yamagandam.label_hi}
        time={`${result.yamagandam.start} - ${result.yamagandam.end}`}
        description={hi ? "स्वामी यम · यात्रा से बचें" : "Ruled by Yama · Avoid travel"}
      />
      <SecondarySlotCard
        label="Gulika Kaal"
        labelHi={result.gulika_kaal.label_hi}
        time={`${result.gulika_kaal.start} - ${result.gulika_kaal.end}`}
        description={
          hi ? "स्वामी शनि · शुभ कार्य से बचें" : "Ruled by Saturn · Avoid auspicious work"
        }
      />
    </div>
  );
}

function SecondarySlotCard({
  label,
  labelHi,
  time,
  description,
}: {
  label: string;
  labelHi: string;
  time: string;
  description: string;
}) {
  return (
    <div className="card-sacred p-5 text-center border-error/20 bg-error/2">
      <span
        lang="hi"
        className="block text-[11px] font-semibold uppercase tracking-wider mb-1 text-error"
      >
        {labelHi}
      </span>
      <span className="block text-[11px] font-semibold uppercase tracking-wider mb-2 text-error">
        {label}
      </span>
      <span className="text-[20px] font-bold text-error font-serif">{time}</span>
      <p className="mt-2 text-caption text-ish-muted">{description}</p>
    </div>
  );
}

function PanchangSummaryCard({
  result,
  dayName,
  language,
}: {
  result: MuhuratResponse;
  dayName: string;
  language: "en" | "hi";
}) {
  // Named by weekday, not "Today's": the same card renders any selected date.
  return (
    <div className="card-sacred p-6 sm:p-8">
      <h2 className="text-[18px] font-bold mb-4 text-ish-dark font-serif">
        {language === "hi"
          ? `पंचांग सारांश · ${DAY_NAMES_HI[dayName] ?? dayName}`
          : `Panchang Summary · ${dayName}`}
      </h2>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        <PanchangFact
          label="Tithi"
          value={result.panchang.tithi.name_en}
          detail={`${result.panchang.tithi.name_hi} · ${pakshaLabel(result)}`}
        />
        <PanchangFact
          label="Nakshatra"
          value={result.panchang.nakshatra.name_en}
          detail={result.panchang.nakshatra.name_hi}
        />
        <PanchangFact
          label="Yoga"
          value={result.panchang.yoga.name_en}
          detail={result.panchang.yoga.name_hi}
        />
        <PanchangFact label="Day" value={result.vara.name_en} detail={result.vara.name_hi} />
      </div>
    </div>
  );
}

function pakshaLabel(result: MuhuratResponse) {
  return result.panchang.tithi.paksha === "shukla" ? "Shukla Paksha" : "Krishna Paksha";
}

function PanchangFact({ label, value, detail }: { label: string; value: string; detail: string }) {
  return (
    <div>
      <span className="block text-[11px] font-semibold uppercase tracking-wider mb-1 text-ish-faint">
        {label}
      </span>
      <span className="text-body font-bold text-ish-dark">{value}</span>
      {/* The engine's Hindi name, beside English (the paksha) on the tithi. */}
      <span className="block text-caption text-ish-faint">{taggedHindi(detail)}</span>
    </div>
  );
}

function KundaliCta({ language }: { language: "en" | "hi" }) {
  return (
    <div className="text-center">
      <Link
        href={language === "hi" ? "/hi/free-kundali/" : "/free-kundali/"}
        className="btn-sacred inline-block font-semibold py-3 px-6 rounded-lg text-body"
      >
        {language === "hi" ? "अपनी मुफ़्त कुंडली बनाएं" : "Get Your Free Kundali"} &rarr;
      </Link>
    </div>
  );
}
